import { asJSONSafe } from "../functions";
import { openDB } from 'idb';

const DB_NAME = 'cache-db';
const STORE_NAME = 'cache';

const cachePrefix = "cache__";

/** Protocol details + edit: same key so list with heavy `sign` payloads is fetched once per scope */
export const PROTOCOL_ACCREDITATIONS_IDB_CACHE_KEY = "protocol-accreditations-list";
export const PROTOCOL_ACCREDITATIONS_IDB_TTL_MIN = 30;

/** Временно: true — не читать/писать IDB для списка знаков (всегда свежий запрос). */
export const PROTOCOL_ACCREDITATIONS_IDB_CACHE_DISABLED = false;

export const getDb = async () => {
    return await openDB(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    });
  };
  
  export const saveToCache = async (key, value) => {
    const db = await getDb();
    await db.put(STORE_NAME, value, key);
  };
  
  export const loadFromCache = async (key) => {
    const db = await getDb();
    return await db.get(STORE_NAME, key);
  };
  
  export const deleteFromCache = async (key) => {
    const db = await getDb();
    await db.delete(STORE_NAME, key);
  };

/**
 * Like CacheService.getOrCreate but persists only to IndexedDB (no localStorage).
 * Use for large payloads (e.g. accreditation images) to avoid QuotaExceededError.
 */
export async function getOrCreateIdbOnly(key, request, getFunc, expirationMinutes) {
  if (key === PROTOCOL_ACCREDITATIONS_IDB_CACHE_KEY && PROTOCOL_ACCREDITATIONS_IDB_CACHE_DISABLED) {
    return await getFunc();
  }

  const svc = CacheService();
  let result;
  try {
    result = await svc.get(key, request);
  } catch (e) {
    console.error(e);
  }
  if (result != null) return result;

  result = await getFunc();
  if (result == null) return result;

  try {
    const expiration = new Date();
    expiration.setMinutes(expiration.getMinutes() + expirationMinutes);
    const cacheItem = {
      expiration,
      value: result,
    };
    const requestKey = request ? JSON.stringify(request) : "";
    const cacheKey = `${cachePrefix}${key}${requestKey}`;
    svc.cache[cacheKey] = cacheItem;
    await saveToCache(cacheKey, cacheItem);
  } catch (e) {
    console.error(e);
  }

  return result;
}

export function CacheService() {
  return {
    cache: {},
    getOrCreate: async function (key, request, getFunc, expirationMinutes) {
        try {
            let result = await this.get(key, request);

            if (result == null) {
                result = await getFunc();
                if (result == null)
                    return result;
                try {
                    await this.set(key, request, result, expirationMinutes);
                } catch (e) {
                    console.error(e);
                    return result;
                }
            }

            return result;
        } catch (e) {
            console.error(e);
        }

        return null;
    },
    get: async function (key, request) {
        const requestKey = request ? JSON.stringify(request) : '';

        const cacheKey = `${cachePrefix}${key}${requestKey}`;

        let result = this.cache[cacheKey];

        if (result == null) {            
            result = await loadFromCache(cacheKey);

            if (result == null) {
                result = localStorage.getItem(cacheKey);
                if (result != null)
                    result = asJSONSafe(result);
            }
            
            if (result != null) {                
                this.cache[cacheKey] = result;
            }
        }

        if (result?.value == null)
            return result;

        let expiration = result.expiration;
        if (expiration.substring)
            expiration = new Date(expiration);

        const date = new Date();

        if (date > expiration)
            return null;

        return result.value;
    },
    set: async function (key, request, value, expirationMinutes) {
        const expiration = new Date();
        expiration.setMinutes(expiration.getMinutes() + expirationMinutes);

        const cacheItem = {
            expiration,
            value
        }

        const requestKey = request ? JSON.stringify(request) : '';
        const cacheKey = `${cachePrefix}${key}${requestKey}`;
        this.cache[cacheKey] = cacheItem;

        try {
            await saveToCache(cacheKey, cacheItem);

            const s = JSON.stringify(cacheItem);
            localStorage.setItem(cacheKey, s);
        } catch (e) {
            console.error(e);
            localStorage.removeItem(cacheKey);
        }
    },
  }
}
